import {
    type FlowCfg,
    type NodeCfg,
    type NodeSocketKindMap,
} from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import { getUID } from '@redviking/argonaut-util/src/getUID';
import type { RequiredSockets, UiNodeSpec } from './UiNodeSpec';
import { createNodeSocket } from '../../sync-utils/create-node-socket';
import { accessor } from '@redviking/argonaut-core-ui/src/store';

const validateAgainstCircularReferences = (flows: Record<string, FlowCfg>, flowCfgId: string, functionalFlowIds: string []): string | null => {
    const flow = flows[flowCfgId];
    if (!flow) {
        return `Flow ID: ${flowCfgId} not found`;
    }

    for (const functionalSelectNode of Object.values(flow.nodes).filter((n): n is NodeCfg<'functionalSelect'> => n.kind === 'functionalSelect')) {
        if (functionalFlowIds.includes(functionalSelectNode.controlValues.flowId)) {
            return `Flow ${flow.name} is invloved in a circular functional select loop`;
        } else {
            const recursiveFlow = flows[functionalSelectNode.controlValues.flowId];
            if (!recursiveFlow) {
                return `Flow ID: ${functionalSelectNode.controlValues.flowId} not found`;
            }
            const recursiveCheck = validateAgainstCircularReferences(flows, recursiveFlow.id, [ ...functionalFlowIds, recursiveFlow.id ]);
            if (recursiveCheck) {
                return recursiveCheck;
            }
        }
    }
    return null;
};

export const functionalSelectUiNodeSpec: UiNodeSpec<'functionalSelect'> = {
    nodeCategory: 'other',
    kindLabel: 'Functional Select',
    validator: node => {
        const errors: Record<string, string> = {};
        if (!node.controlValues.flowId) {
            errors.flowId = 'A flow selection is required';
        }
        const entityData = accessor.entityAsType('appletDesignVersion');
        const flows = entityData?.config.flows;
        if (!flows) {
            errors.flowId = 'No flow found';
        } else {
            const sourceFlowId = Object.values(flows).find(f => Object.values(f.nodes).find(n => n.id === node.id))?.id;
            if (!sourceFlowId) {
                errors.flowId = 'Source flow not found';
            } else {
                const result = validateAgainstCircularReferences(flows, sourceFlowId, [ sourceFlowId ]);
                if (result) {
                    errors.flowId = result;
                }
            }
        }
        return Object.keys(errors).length ? errors : true;
    },
    requiredSockets: node => {
        const ret: ReturnType<RequiredSockets<typeof node.kind, NodeSocketKindMap[typeof node.kind]>> = {
            required: [],
            // this conditionallyRequired statement basically says at least one socket must be wired up
            conditionallyRequired: [
                {
                    kind: 'or',
                    availableOptions: [
                        'finish',
                        'start',
                        ...node.controlValues.inputs.map((_, idx) => `input${idx}`) as `input${number}`[],
                        ...node.controlValues.outputs.map((_, idx) => `output${idx}`) as `output${number}`[],
                    ],
                },
            ],
        };
        return ret;
    },
    cfg: () => ({
        id: getUID(),
        kind: 'functionalSelect',
        disabled: false,
        controlValues: {
            flowId: '',
            inputs: [],
            outputs: [],
        },
    }),
    controls: getNodeCfg => ({
        flowId: {
            cfg: {
                kind: 'functionalFlowSelect',
                label: 'Select Function Flow',
                getValue: nodeCfg => {
                    if (nodeCfg.kind !== 'functionalSelect') {
                        return null;
                    }
                    return nodeCfg.controlValues.flowId || null;
                },
                onSelect: (flowId: string | null) => {
                    if (!flowId) {
                        return;
                    }
                    const entityData = accessor.entityAsType('appletDesignVersion');
                    const selectedFlowCfg = (entityData?.config.flows || {})[flowId];
                    if (!selectedFlowCfg) {
                        return;
                    }
                    const functionalInputsNode = Object.values(selectedFlowCfg.nodes).find((n): n is NodeCfg<'functionalInputs'> => n.kind === 'functionalInputs');
                    if (!functionalInputsNode) {
                        throw new Error(`Functional inputs node not found inside of function flow "${selectedFlowCfg.name}" with id "${flowId}"`);
                    }
                    // NOTE: calling `find` here even though there can be more than one `functionalOutputs` node because all their values should be synced up
                    const functionalOutputsNode = Object.values(selectedFlowCfg.nodes).find((n): n is NodeCfg<'functionalOutputs'> => n.kind === 'functionalOutputs');
                    if (!functionalOutputsNode) {
                        throw new Error(`Functional output node not found inside of function flow "${selectedFlowCfg.name}" with id "${flowId}"`);
                    }
                    const controlValues: NodeCfg<'functionalSelect'>['controlValues'] = {
                        ...getNodeCfg().controlValues,
                        flowId,
                        inputs: functionalInputsNode.controlValues.inputs,
                        outputs: functionalOutputsNode.controlValues.outputs,
                    };
                    const newNodeCfg = {
                        ...getNodeCfg(),
                        controlValues,
                    };

                    createNodeSocket({
                        newNodeCfg,
                    });
                },
            },
            nodeCfg: getNodeCfg(),
            value: getNodeCfg().controlValues.flowId,
        },
    }),
    inputs: cfg => {
        const ret: ReturnType<UiNodeSpec<'functionalSelect'>['inputs']> = {
            start: { socket: 'event', label: 'Start' },
        };
        cfg.controlValues.inputs.forEach((input, idx) => {
            ret[`input${idx}`] = { socket: input.dataType, label: input.name };
        });
        return ret;
    },
    outputs: cfg => {
        const ret: ReturnType<UiNodeSpec<'functionalSelect'>['outputs']> = {
            finish: { socket: 'event', label: 'Finish' },
        };
        cfg.controlValues.outputs.forEach((output, idx) => {
            ret[`output${idx}`] = { socket: output.dataType, label: output.name };
        });
        return ret;
    },
};
